import { cmp } from '@/lib/money'
import type { FundPeriod, FundStockCount, SectorSlice } from '@/schemas/mutual-funds'

/**
 * The defaults a Mutual funds URL falls back to when it names nothing — read
 * by the route loaders AND the screens, so they live here rather than in a
 * component module (docs/route-loading.md, Steps §2: a loader must never
 * import one).
 */

/** The trends chart draws at most this many lines at once — the mock's limit (`cbe`); more is unreadable. */
export const MAX_TREND_FUNDS = 6

/** How many funds the trends chart opens with when `?funds=` names none. */
export const DEFAULT_TREND_FUND_COUNT = 3

/**
 * The trends chart's opening selection: the funds holding the most stocks.
 * Sorted here rather than trusting the response's order, stably, so a tie
 * keeps the backend's own tie-break.
 */
export const defaultTrendFunds = (rows: readonly FundStockCount[]): string[] =>
  [...rows]
    .sort((a, b) => b.stockCount - a.stockCount)
    .slice(0, DEFAULT_TREND_FUND_COUNT)
    .map((row) => row.symbol)

/**
 * Value descending, a missing value sorting last — the order a donut and its
 * legend draw a sector split in, largest slice first. Exported so every
 * screen that needs "the largest slice" (Industry holdings' legend, Analytics'
 * sector dialog) reads off the same order instead of each restating it.
 */
export const compareSlicesByValueDesc = (a: SectorSlice, b: SectorSlice): number => {
  if (a.value === null) return b.value === null ? 0 : 1
  if (b.value === null) return -1
  return cmp(b.value, a.value)
}

/**
 * The sector Industry holdings lists companies for: the `?sector=` code when it names one of the
 * period's classified slices, else null — All sectors, the page's opening view since the
 * 2026-11-03 mock. A stale code (a previous period's, still in the URL) reads as All sectors too,
 * rather than asking `/companies/` for a code that cannot match.
 */
export const sectorOrAll = (
  code: string | undefined,
  slices: readonly SectorSlice[],
): string | null =>
  code !== undefined && slices.some((slice) => slice.sector === code) ? code : null

/**
 * A stale `?page=N` from a shorter list (a filter narrowed it, a shorter
 * month) lands on the last real page, not an empty table that would read as
 * "no results" — Fund rankings, and every other list this feature pages
 * client-side over one response already in hand.
 */
export const clampPage = (page: number, total: number, pageSize: number): number =>
  Math.min(page, Math.max(1, Math.ceil(total / pageSize)))

/**
 * The label a period select should actually write to `?period=`: `undefined`
 * when it names the newest period, since that is the URL's own default —
 * writing it out would open a second, identical cache entry and stop a link
 * without `period` and one naming it explicitly from being the same URL.
 */
export const canonicalPeriod = (
  label: string,
  periods: readonly FundPeriod[],
): string | undefined => (periods[0]?.label === label ? undefined : label)

/**
 * The `?period=` a holdings read may actually send: the URL's label when `/periods/` lists it,
 * else `null` (the latest). Since 2026-10-29 the backend answers an unknown `period` with a 400
 * `MUTUAL_FUND_PERIOD_UNKNOWN` instead of an empty payload, so a hand-edited or stale label must
 * read as the latest period rather than fail the page. The loader and the screen both read
 * through this, so they land on the same cache key.
 */
export const knownPeriod = (
  period: string | undefined,
  periods: readonly FundPeriod[],
): string | null =>
  period !== undefined && periods.some((option) => option.label === period) ? period : null
